import { z } from 'zod';
import { JurisdictionSchema, type Jurisdiction } from '../enums/index.js';
import { describeAdminCorpusCoverage } from './adminCorpusCoverage.js';

// Re-export for convenience — historically each agent imported a local Jurisdiction
// from apps/backend/types/hector-agent-edition-v3 ; the canonical one lives in enums/.
export { JurisdictionSchema, type Jurisdiction };

/**
 * Canonical Zod schemas + types for the `search_jurisprudence` agent tool.
 *
 * This tool is exposed to THREE callers :
 *   - Edition V3        (apps/addin-word, via apps/backend/services/hector-agent-edition-v3)
 *   - Case Discovery V3 (apps/legalworkspace, via apps/backend/services/hector-agent-case-discovery-v3)
 *   - subagent legal_research (apps/backend/services/agent-runner/tools/searchJudilibre.ts)
 *
 * Contract (since 2026-07-03, suppression du mode quick/deep) :
 *   **UN appel = UNE question.** Il n'y a plus de paramètre `mode` — l'ancien
 *   comportement `deep` (1-3 questions dans un même job) est remplacé par
 *   PLUSIEURS tool_calls en parallèle, une question par appel. Le pipeline
 *   backend n'a plus qu'un seul comportement (ex-`quick` : top-20 décisions,
 *   réponse synthétique unique).
 *
 * Le wrapper backend (`apps/backend/services/jp-analysis/wrappers/agentToolWrapper.ts`)
 * fait la coercion à la frontière de parsing : `question` (string) → interne
 * `questions: [{ id: 'q1', text, context }]` (le pipeline interne et le contrat
 * WebSocket restent list-based, à 1 élément). Les formes legacy (`questions[]`,
 * `mode`) émises par le LLM en rejouant de vieilles conversations sont tolérées :
 * `questions[0]` est retenue, `mode` est ignoré.
 *
 * Hub: docs/features/jurisprudence-search/
 */

// ===========================================================================
// Sub-types
// ===========================================================================

/** Question de droit (forme interne, list-based) portée par le jobData Redis + le contrat WS. */
export const SearchJurisprudenceQuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  /**
   * Contexte de la question (3-5 lignes) — utilisé pour scorer la pertinence
   * factuelle des JP. Optionnel (question isolée sans dossier).
   */
  context: z.string().optional(),
});
export type SearchJurisprudenceQuestion = z.infer<typeof SearchJurisprudenceQuestionSchema>;

/** Fondement juridique (code + article) identifié par l'agent. */
export const SearchJurisprudenceFondementSchema = z.object({
  code: z.string(),
  article: z.string(),
  /**
   * Texte intégral de l'article — Case Discovery V3 le rend obligatoire dans le
   * prompt LLM mais l'arrivée du contenu via LLM peut être manquante. Le wrapper
   * traite l'absence en fallback empty-string pour ne pas casser le pipeline.
   */
  content: z.string().optional(),
  /** Identifiant lu dans Légifrance, recoupé avec le texte parent par le worker. */
  legifranceId: z.string().optional(),
});
export type SearchJurisprudenceFondement = z.infer<typeof SearchJurisprudenceFondementSchema>;

// ===========================================================================
// Tool input (canonical) — what the LLM emits via tool_use
// ===========================================================================

/** Canonical tool input schema : UNE question par appel, pas de mode. */
export const SearchJurisprudenceToolInputSchema = z.object({
  question: z.string().min(1),
  /** Contexte juridique + factuel de la question (3-5 lignes). */
  context: z.string().optional(),
  jurisdictions: z.array(JurisdictionSchema).min(1),
  // Prose de la description = « OBLIGATOIRE » (nudge qualité : ancrer la recherche
  // sur des articles). MAIS on ne bloque pas l'appel si absent — non-`required` côté
  // JSON Schema (SEARCH_JURISPRUDENCE_TOOL_INPUT_SCHEMA.required), et le wrapper
  // retombe sur `[]` (agentToolWrapper.ts: `ensureArray(rawParams.fondements_juridiques, [])`).
  // Le `.default([])` fait dire au Zod la même chose que ces deux couches.
  fondements_juridiques: z.array(SearchJurisprudenceFondementSchema).default([]),
});
export type SearchJurisprudenceToolInput = z.infer<typeof SearchJurisprudenceToolInputSchema>;

// ===========================================================================
// Tool output — what the wrapper returns to the LLM
// ===========================================================================

/**
 * Tool output : fire-and-forget job creation.
 *
 * The wrapper stores the job in Redis under `job:{jobId}:jurisprudence` and returns
 * a placeholder result. The agent's MasterLoop detects `status: 'pending_validation'`
 * and DEFERS `addToolResult` — the search runs asynchronously (the front auto-submits
 * the proposed question since #289, no manual validation gate).
 *
 * Both shapes are unioned because executors emit different error variants when
 * something goes wrong client-side (no Redis, no question).
 */
export const SearchJurisprudenceToolOutputSuccessSchema = z.object({
  jobId: z.string(),
  status: z.literal('pending_validation'),
});
export const SearchJurisprudenceToolOutputErrorSchema = z.object({
  error: z.literal(true),
  message: z.string(),
});
export const SearchJurisprudenceToolOutputSchema = z.union([
  SearchJurisprudenceToolOutputSuccessSchema,
  SearchJurisprudenceToolOutputErrorSchema,
]);
export type SearchJurisprudenceToolOutputSuccess = z.infer<
  typeof SearchJurisprudenceToolOutputSuccessSchema
>;
export type SearchJurisprudenceToolOutputError = z.infer<
  typeof SearchJurisprudenceToolOutputErrorSchema
>;
export type SearchJurisprudenceToolOutput = z.infer<typeof SearchJurisprudenceToolOutputSchema>;

// ===========================================================================
// Anthropic-compatible JSON Schema (input_schema)
// ===========================================================================

/**
 * JSON Schema declaration for the `search_jurisprudence` tool input — shape passed
 * to the LLM as the tool's `parameters`/`input_schema`.
 *
 * Single source of truth consommée À L'IDENTIQUE par les deux agents maîtres :
 *   - Case Discovery V3 (hector-agent-case-discovery-v3/llm/ToolDefinitions.ts)
 *   - Edition V3        (hector-agent-edition-v3/llm/ToolDefinitions.ts)
 * Edition l'importe depuis #424 et a hérité du passage mono-question (#524, fin du
 * mode quick/deep) sans toucher son fichier — c'est l'intérêt de la source unique.
 *
 * Le subagent `legal_research` (agent-runner/tools/searchJudilibre.ts) NE consomme
 * PAS ce schéma : contrat local minimal `{ question }`, scope judiciaire-only.
 * The values are inert (no SDK dependency) so this stays in shared-types.
 */
export const SEARCH_JURISPRUDENCE_TOOL_NAME = 'search_jurisprudence' as const;

export const SEARCH_JURISPRUDENCE_TOOL_INPUT_SCHEMA = {
  type: 'object',
  properties: {
    question: {
      type: 'string',
      // La formulation en QUALIFICATION JURIDIQUE est la première ligne de défense du
      // caviardage : la question est embeddée en clair et envoyée (caviardée) au reranker
      // externe — un nom de partie que le dictionnaire de caviardage ignore (dossier neuf,
      // adversaire absent de case_parties) ne sort pas s'il n'a jamais été écrit.
      // Cf. jp-analysis/retrieval/anonymizeRerankQuery.ts.
      description:
        "UNE SEULE question de droit, complète et précise (pas de mots-clés). Pour couvrir plusieurs questions, lance plusieurs appels search_jurisprudence en parallèle. Formule-la en termes de QUALIFICATION JURIDIQUE : pas de nom de partie, pas de montant, de date ni de référence propres au dossier. Écris « un salarié licencié pour faute grave », pas « M. Dupont, licencié le 3 mars 2024 ». Les faits qualifiés restent utiles à la recherche — seuls les éléments identifiants sont à écarter.",
    },
    context: {
      type: 'string',
      description:
        "Contexte de la question en 3-5 lignes. Chaîne vide si la question est posée hors contexte (question isolée sans dossier).\nSi un dossier ou une discussion est en cours, inclure :\n- Le contexte juridique : quel argument ou raisonnement juridique est en jeu, ce que l'avocat cherche à démontrer.\n- Le contexte factuel : les faits concrets du dossier pertinents pour cette question (parties, situation, événements clés).\nCe contexte permet d'évaluer la pertinence des JP trouvées à la fois sur le plan du droit (principe juridique applicable) et sur le plan des faits (proximité factuelle avec le dossier, utile pour les JP d'espèce CA/TJ/TCOM ou CAA/TA).",
    },
    jurisdictions: {
      type: 'array',
      items: {
        type: 'string',
        enum: ['CC', 'CA', 'TJ', 'TCOM', 'CE', 'CAA', 'TA'],
      },
      description:
        "Juridictions sur lesquelles effectuer la recherche. Judiciaire : CC (Cour de cassation), CA (Cour d'appel), TJ (Tribunal judiciaire), TCOM (Tribunal de commerce). Administratif : CE (Conseil d'État), CAA (Cour administrative d'appel), TA (Tribunal administratif). Défaut : [\"CC\", \"CA\", \"TJ\", \"TCOM\"]. Ajouter CE/CAA/TA uniquement si le dossier relève du droit administratif.",
    },
    fondements_juridiques: {
      type: 'array',
      description:
        "Fondements juridiques (code + article) identifiés comme pertinents. Le contenu textuel sera récupéré automatiquement si disponible.",
      items: {
        type: 'object',
        properties: {
          code: {
            type: 'string',
            description: 'Nom du code (ex: "Code civil", "Code du travail")',
          },
          article: {
            type: 'string',
            description: 'Numéro d\'article (ex: "1240", "L. 442-6")',
          },
          content: {
            type: 'string',
            description:
              "Texte intégral de l'article de loi, tel que rendu par legifrance_get_article. Indispensable pour contextualiser la recherche.",
          },
          legifranceId: {
            type: 'string',
            description:
              "Identifiant LEGIARTI exact de l'article déjà lu avec legifrance_get_article. Recopie cet identifiant lorsqu'il est disponible ; ne l'invente jamais. Il évite une recherche ambiguë du texte.",
          },
        },
        required: ['code', 'article', 'content'],
      },
    },
  },
  required: ['question', 'jurisdictions'],
} as const satisfies Record<string, unknown>;

/**
 * Tool description shown to the LLM.
 *
 * Note : individual agents may override / augment the description in their own
 * ToolDefinitions. The canonical description here states the baseline contract.
 */
export const SEARCH_JURISPRUDENCE_TOOL_DESCRIPTION = `Recherche de jurisprudence sur UNE question de droit précise (~10-15s par appel).

UN appel = UNE question. Pour couvrir plusieurs questions ou approfondir un sujet large, lance PLUSIEURS appels dans la MÊME réponse (max 4, en parallèle) — une question différente par appel.
Appelle directement sans lister la question dans ton message — elle est affichée via l'interface interactive.
Formule une question complète de droit, pas de mots-clés.

Fondements juridiques (OBLIGATOIRE) :
Renseigne 5-10 articles avec code, numéro et texte intégral (copie depuis legifrance_get_article, l'extrait de legifrance_search_code est partiel). Avant l'appel, vérifie les articles avec les outils Légifrance ; ne cite jamais un article de mémoire.

Les résultats sont des fiches d'analyse (synthèse, attendu clé), pas le texte des décisions : ils prouvent qu'une décision existe, pas ce qu'elle juge. Lis-la avec search_jp_by_id avant de la dire vérifiée.

${describeAdminCorpusCoverage()}`;
